/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, ElementRef, HostListener, inject, OnInit, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { AuthenticationService } from '@alfresco/adf-core';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { WhatsNewService } from '../version/whats-new.service';
import { OnboardingService, TourPlacement } from './onboarding.service';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Room around the highlighted element, and between it and the card. */
const PAD = 6;
const GAP = 14;
const MARGIN = 16;
const CARD_WIDTH = 340;
/** Below this width the card sits at the bottom of the screen instead of beside its target. */
const NARROW = 640;
/** How long to wait for the shell to draw before offering the tour. */
const SHELL_WAIT_MS = 10_000;

/**
 * The first-use tour: a card beside each thing worth knowing, with the rest of
 * the screen dimmed. Mounted once beside the command palette.
 *
 * It offers itself once to each person, after they sign in and the app shell
 * is on screen — never over the sign-in page or a public link. Esc or "ข้าม"
 * ends it; the help menu starts it again.
 */
@Component({
  selector: 'app-onboarding-tour',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './onboarding-tour.component.html',
  styleUrls: ['./onboarding-tour.component.scss']
})
export class OnboardingTourComponent implements OnInit {
  readonly tour = inject(OnboardingService);
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly whatsNew = inject(WhatsNewService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('card') private readonly card?: ElementRef<HTMLElement>;

  /** The highlighted area; null for a step in the middle of the screen. */
  readonly spot = signal<Box | null>(null);
  readonly cardTop = signal(0);
  readonly cardLeft = signal(0);
  readonly docked = signal(false);

  private offered = false;

  constructor() {
    // each new step: bring its target into view, then place the card
    effect(() => {
      if (this.tour.active() && this.tour.step()) {
        setTimeout(() => {
          this.target()?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          this.place();
          this.card?.nativeElement.focus();
        });
      }
    });
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.offer());
    this.auth.onLogout.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.offered = false;
      if (this.tour.active()) {
        this.tour.active.set(false);
      }
    });
  }

  /**
   * Once per session, to a signed-in person who has not seen it, once the shell
   * is drawn. Never in a browser driven by automation: end-to-end tests start
   * with empty storage every run, and the tour's backdrop would sit over every
   * page they click. The help menu still starts it there.
   */
  private offer(): void {
    if (this.offered || this.tour.active() || !this.auth.isLoggedIn() || navigator.webdriver) {
      return;
    }
    const user = this.auth.getUsername() ?? '';
    if (!this.tour.isNew(user)) {
      return;
    }
    this.offered = true;
    const startedAt = Date.now();
    const wait = () => {
      if (!this.auth.isLoggedIn()) {
        this.offered = false;
      } else if (document.querySelector('.aca-shell-header')) {
        // a moment for the sidebar's entries to arrive after the header
        setTimeout(() => this.tour.start(user), 600);
      } else if (Date.now() - startedAt < SHELL_WAIT_MS) {
        setTimeout(wait, 400);
      } else {
        // no shell (a public link, the viewer): try again on the next page
        this.offered = false;
      }
    };
    wait();
  }

  next(): void {
    if (this.tour.last()) {
      this.end();
    } else {
      this.tour.next();
    }
  }

  back(): void {
    this.tour.back();
  }

  /** Finished or skipped — and since the tour points at the help menu, the update news counts as read. */
  end(): void {
    this.tour.finish();
    this.whatsNew.markSeen();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (!this.tour.active()) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.end();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.back();
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  place(): void {
    if (!this.tour.active()) {
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(CARD_WIDTH, vw - 2 * MARGIN);
    const height = this.card?.nativeElement.offsetHeight || 220;
    const el = this.target();
    const rect = el?.getBoundingClientRect();

    if (!rect || rect.width === 0) {
      this.spot.set(null);
      this.docked.set(false);
      this.cardTop.set(Math.max(MARGIN, (vh - height) / 2));
      this.cardLeft.set((vw - width) / 2);
      return;
    }

    this.spot.set({ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + 2 * PAD, height: rect.height + 2 * PAD });

    if (vw < NARROW) {
      this.docked.set(true);
      // away from the target: at the bottom unless the target is down there
      this.cardTop.set(rect.top > vh / 2 ? MARGIN : vh - height - MARGIN);
      this.cardLeft.set(MARGIN);
      return;
    }
    this.docked.set(false);

    const { top, left } = OnboardingTourComponent.position(rect, this.tour.step()?.placement ?? 'bottom', width, height, vw, vh);
    this.cardTop.set(top);
    this.cardLeft.set(left);
  }

  /** Beside the target on the side asked for, falling back below or above when it does not fit; always on screen. */
  static position(rect: Box, placement: TourPlacement, width: number, height: number, vw: number, vh: number): { top: number; left: number } {
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));
    const right = rect.left + rect.width + PAD + GAP;
    const leftSide = rect.left - PAD - GAP - width;
    let top: number;
    let left: number;

    if (placement === 'right' && right + width <= vw - MARGIN) {
      left = right;
      top = rect.top + rect.height / 2 - height / 2;
    } else if (placement === 'left' && leftSide >= MARGIN) {
      left = leftSide;
      top = rect.top + rect.height / 2 - height / 2;
    } else {
      left = rect.left + rect.width / 2 - width / 2;
      const below = rect.top + rect.height + PAD + GAP;
      top = below + height <= vh - MARGIN ? below : rect.top - PAD - GAP - height;
    }
    return { top: clamp(top, MARGIN, vh - height - MARGIN), left: clamp(left, MARGIN, vw - width - MARGIN) };
  }

  private target(): HTMLElement | null {
    const selector = this.tour.step()?.target;
    const el = selector ? document.querySelector(selector) : null;
    return el instanceof HTMLElement ? el : null;
  }
}
